import PropTypes from "prop-types";
/**
 * @typedef {Object} MetaObject
 *
 * @property {string} name - Name of the meta tag
 * @property {string} property - Optional property of the meta tag, for OpenGraph values
 * @property {string} content - Content of the meta tag
 */

/**
 * The makeMeta function generates the object that is passed to the Helmet as metadata.
 * Each page was checked to validate that ell the meta tags had no different values, and that
 * the auto-generated ones are what would be expected to be inputted here.
 *
 * It should be safe to implement this for all pages.
 *
 * @param {Object} props
 * @param {string} props.title - Title of the page (Already transformed)
 * @param {string} props.description - Description of the page
 * @param {string} props.imagePath - Path to the OG:Image
 * @param {string} props.imageAlt - Alt text for the OG:Image
 * @param {string} props.imageDate - Date for the image, formatted as MMDDYY. If not provided, 010125 will be used.
 * @param {string} props.path - Path of the page
 * @param {boolean} props.noIndex - If true, "noindex nofollow" will be added to the title. Default false.
 * @param {Array} props.customTags - Can include any other custom tags
 *
 * @returns {MetaObject[]} - Returns an array of meta tags for the page.
 */
const makeMeta = ({ title, description, imagePath, imageAlt, imageDate, path, noIndex, customTags }) => {
  const defaultOgImage = "/images/wafd-logo.png";
  const isNewOgImagePath = imagePath && imagePath.startsWith("/og-images");

  const meta = [
    {
      name: "application-name",
      content: "WaFd Bank public website"
    },
    {
      name: "title",
      property: "og:title",
      content: title
    },
    {
      name: "description",
      property: "og:description",
      content: description
    },
    {
      property: "og:url",
      content: "https://www.wafdbank.com" + path // Used as an ID on OpenGraph
    },
    {
      property: "og:type",
      content: "website" // If left blank, it assumes website. We define it so it's explicit. https://ogp.me/#type_website
    },
    {
      property: "og:site_name",
      content: "wafdbank"
    },
    {
      property: "og:image",
      content: `https://www.wafdbank.com${imagePath ?? defaultOgImage}`
    },
    {
      property: "og:image:width",
      content: isNewOgImagePath ? "1200" : "250"
    },
    {
      property: "og:image:height",
      content: isNewOgImagePath ? "627" : "250"
    },
    {
      property: "og:image:alt",
      content: imageAlt || "WaFd Bank logo."
    }
  ];

  if (noIndex) {
    meta.push({
      name: "robots",
      content: "noindex nofollow"
    });
  }

  if (customTags.length > 0) {
    meta.push(...customTags);
  }

  if (isNewOgImagePath) {
    meta.find((item) => item.property === "og:image")["data-og-image-date"] = imageDate ?? "010125";

    if (!imageDate) {
      console.warn(`makeMeta: No image date provided for ${path}, but using new format. Defaulting to 010125.`);
    }
  }

  return meta;
};

makeMeta.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.string,
  path: PropTypes.string.isRequired,
  noIndex: PropTypes.bool,
  customTags: PropTypes.array
};

export default makeMeta;
